/**
 * Formatadores de datas para pt-BR.
 *
 * Aceita Date, ISO string, ou timestamp number. Sempre retorna string.
 * Inválidos retornam "—" (consistente com format.ts).
 */

type DateInput = Date | string | number | null | undefined;

/**
 * Parseia ISO/SQLite string de forma defensiva, sempre interpretando como UTC.
 *
 * Fix BUG-V2-048 (timezone UTC): SQLite `datetime('now')` produz string sem `T`
 * e sem `Z` no formato `YYYY-MM-DD HH:MM:SS`. Em alguns browsers, `new Date()`
 * parseia isso como LOCAL time, gerando offset de 3h (BRT). Resultado: notif
 * recém-criada aparecia "em 3h" no relativo.
 *
 * Detecção:
 *   - Tem `Z` ou offset (`+03:00`, `-05:00`) → ISO válido, devolve direto
 *   - SQLite (`YYYY-MM-DD HH:MM:SS[.fff]`) sem timezone → assume UTC + adiciona `Z`
 *   - ISO com `T` mas sem `Z`/offset → assume UTC + adiciona `Z`
 *
 * Inputs não-string passam direto (Date, number).
 *
 * NÃO REVERTER: garante consistência entre SQLite `datetime('now')` (sem Z)
 * e código futuro que use `strftime('%Y-%m-%dT%H:%M:%fZ', 'now')` (com Z).
 *
 * @example
 *   parseIsoSafe('2026-05-24 12:34:56')         // → Date UTC 12:34:56
 *   parseIsoSafe('2026-05-24T12:34:56Z')        // → Date UTC 12:34:56
 *   parseIsoSafe('2026-05-24T12:34:56.123Z')    // → Date UTC 12:34:56.123
 *   parseIsoSafe('2026-05-24T12:34:56-03:00')   // → Date UTC 15:34:56
 *   parseIsoSafe('2026-05-24T12:34:56')         // → Date UTC 12:34:56 (assume UTC)
 */
export function parseIsoSafe(input: string): Date {
  // Normaliza: SQLite usa espaço entre data e hora, ISO usa T
  let normalized = input.trim();

  // Já tem timezone explícito? (Z no fim ou ±HH:MM em qualquer lugar após o T/space)
  const hasZ = /Z$/i.test(normalized);
  // Offset: precisa ser DEPOIS da hora (não confundir com `-` da data 2026-05-24)
  // Procuramos `+HH:MM`/`-HH:MM` ou `+HHMM`/`-HHMM` no final
  const hasOffset = /[+-]\d{2}:?\d{2}$/.test(normalized);

  if (!hasZ && !hasOffset) {
    // Sem timezone → assume UTC, normaliza espaço → T se necessário, adiciona Z
    if (normalized.includes(' ') && !normalized.includes('T')) {
      normalized = normalized.replace(' ', 'T');
    }
    normalized = `${normalized}Z`;
  }

  return new Date(normalized);
}

/**
 * O fuso da casa (#484, 04/10/2026). Toda data e hora que o usuário LÊ sai neste
 * fuso — ❌ nunca o do computador de quem abre a tela.
 */
export const FUSO_BRASILIA = 'America/Sao_Paulo';

/**
 * Como um valor deve ser lido antes de virar texto:
 *  - `instante`: um momento no tempo (timestamp do servidor, `new Date()`) →
 *    mostrado no relógio de Brasília;
 *  - `escrito`: uma data de calendário ('2026-08-05') ou um horário marcado
 *    ('2026-08-05T14:30') → mostrado EXATAMENTE como foi escrito, em qualquer
 *    fuso. Guardado como UTC e formatado em UTC: os dígitos não andam.
 */
interface Leitura {
  d: Date;
  fuso: typeof FUSO_BRASILIA | 'UTC';
}

function ler(input: DateInput): Leitura | null {
  if (input === null || input === undefined || input === '') return null;
  let leitura: Leitura;
  if (input instanceof Date) {
    leitura = { d: input, fuso: FUSO_BRASILIA };
  } else if (typeof input === 'string') {
    // URGENTE-ORCAMENTISTA (05/08): string SÓ-DATA ('YYYY-MM-DD') é data de
    // CALENDÁRIO, não instante — mandá-la pelo parseIsoSafe anexava Z
    // (meia-noite UTC) e o Intl devolvia o DIA ANTERIOR em Brasília. Foi assim
    // que a data da proposta '2026-08-05' apareceu "04/08/2026 às 21:00" pro
    // orçamentista (card #1043). O dia formatado é o dia escrito, em qualquer fuso.
    const soData = /^(\d{4})-(\d{2})-(\d{2})$/.exec(input.trim());
    // A-3 (AUDITORIA-COMERCIAL): o IRMÃO que o fix #76 não varreu. O formato
    // EXATO do <Calendario>/datetime-local ('YYYY-MM-DDTHH:MM', sem segundos
    // e sem zona) é WALL-CLOCK — agendou 09:30, é 09:30. Timestamps do
    // servidor (com Z, offset, segundos ou espaço) continuam no parseIsoSafe —
    // são instantes.
    const wallClock = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(input.trim());
    if (soData) {
      leitura = {
        d: new Date(Date.UTC(Number(soData[1]), Number(soData[2]) - 1, Number(soData[3]))),
        fuso: 'UTC',
      };
    } else if (wallClock) {
      leitura = {
        d: new Date(
          Date.UTC(
            Number(wallClock[1]),
            Number(wallClock[2]) - 1,
            Number(wallClock[3]),
            Number(wallClock[4]),
            Number(wallClock[5]),
          ),
        ),
        fuso: 'UTC',
      };
    } else {
      leitura = { d: parseIsoSafe(input), fuso: FUSO_BRASILIA };
    }
  } else {
    leitura = { d: new Date(input), fuso: FUSO_BRASILIA };
  }
  return Number.isNaN(leitura.d.getTime()) ? null : leitura;
}

// Caches Intl.DateTimeFormat (instanciação custosa)
const _dateFormatters = new Map<string, Intl.DateTimeFormat>();

function getDateFormatter(options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = JSON.stringify(options);
  let f = _dateFormatters.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat('pt-BR', options);
    _dateFormatters.set(key, f);
  }
  return f;
}

/**
 * 🔴 A FUNÇÃO ÚNICA (#484): toda data/hora que vai para a tela passa por aqui.
 * Recebe as mesmas opções do `Intl.DateTimeFormat` (o formato não muda) e
 * decide SOZINHA o fuso — o chamador não passa `timeZone`. A trava
 * `scripts/ci-data-na-tela.mjs` reprova `toLocale*String`/`Intl.DateTimeFormat`
 * de data fora deste módulo.
 *
 * formatarData('2026-08-05T17:30:00Z', { hour: '2-digit', minute: '2-digit' }) → "14:30"
 * formatarData('2026-08-05', { day: '2-digit', month: 'long' })                → "05 de agosto"
 */
export function formatarData(input: DateInput, opcoes: Intl.DateTimeFormatOptions): string {
  const l = ler(input);
  if (!l) return '—';
  return getDateFormatter({ ...opcoes, timeZone: l.fuso }).format(l.d);
}

/**
 * O formato que `toLocaleString('pt-BR')` sem opções escrevia ("04/10/2026, 16:09:09"),
 * agora no relógio de Brasília. Existe para a troca não mudar o formato de nenhuma tela.
 */
export function formatDataHoraSegundos(input: DateInput): string {
  return formatarData(input, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * Para um dia de CALENDÁRIO montado na tela com `new Date(ano, mes, dia)` (as
 * grades de mês e semana): usa o dia que a grade escreveu, não o instante.
 * ❌ Não use com timestamp do servidor — esse é `formatarData`.
 */
export function formatarDiaDaGrade(dia: Date, opcoes: Intl.DateTimeFormatOptions): string {
  if (Number.isNaN(dia.getTime())) return '—';
  const escrito = new Date(Date.UTC(dia.getFullYear(), dia.getMonth(), dia.getDate(), 12));
  return getDateFormatter({ ...opcoes, timeZone: 'UTC' }).format(escrito);
}

/** A hora (0–23) e o minuto de agora — ou de um instante — no relógio de Brasília. */
export function horaEmBrasilia(d: Date = new Date()): { hora: number; minuto: number; diaDaSemana: number } {
  const partes = getDateFormatter({
    timeZone: FUSO_BRASILIA,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(d);
  const v = (t: string) => Number(partes.find((p) => p.type === t)?.value ?? NaN);
  // O dia da semana sai da DATA de Brasília (e não do nome que o Intl escreve, que muda entre navegadores).
  const diaDaSemana = new Date(Date.UTC(v('year'), v('month') - 1, v('day'))).getUTCDay();
  return { hora: v('hour'), minuto: v('minute'), diaDaSemana };
}

// ============================================================
// DATA / HORA
// ============================================================

/**
 * Formata data no padrão BR (DD/MM/AAAA).
 *
 * formatDate(new Date(2026, 4, 16))   → "16/05/2026"
 * formatDate('2026-05-16')            → "16/05/2026"
 * formatDate(null)                    → "—"
 */
export function formatDate(input: DateInput): string {
  return formatarData(input, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/**
 * Formata data com nome do mês escrito (16 de maio de 2026).
 *
 * formatDateLong(new Date(2026, 4, 16)) → "16 de maio de 2026"
 */
export function formatDateLong(input: DateInput): string {
  return formatarData(input, { day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * Formata hora (HH:MM).
 *
 * formatTime(new Date(2026, 4, 16, 14, 30)) → "14:30"
 */
export function formatTime(input: DateInput): string {
  return formatarData(input, { hour: '2-digit', minute: '2-digit' });
}

/**
 * Data + hora (16/05/2026 às 14:30).
 *
 * formatDateTime(new Date(2026, 4, 16, 14, 30)) → "16/05/2026 às 14:30"
 */
export function formatDateTime(input: DateInput): string {
  if (!ler(input)) return '—';
  return `${formatDate(input)} às ${formatTime(input)}`;
}

/**
 * Formata uma string de AGENDAMENTO vinda do input `datetime-local`
 * (wall-clock LOCAL do usuário, `YYYY-MM-DDTHH:MM` SEM timezone) EXATAMENTE
 * como foi digitada — 14:30 aparece 14:30, sem conversão de fuso.
 *
 * BUG A1: `parseIsoSafe` anexa `Z` a essas strings (herança das strings UTC do
 * SQLite) e o formatter usa o fuso do runtime → o horário agendado saía
 * deslocado (14:00 → 12:00). Aqui lemos os dígitos direto, à prova de fuso.
 *
 * - `2026-08-10T14:30`  → "10/08/2026 às 14:30"  (wall-clock literal)
 * - `2026-08-11`        → "11/08/2026"           (só data)
 * - com `Z`/offset      → delega ao formatDateTime (instante → Brasília)
 */
export function formatAgendamento(input: string | null | undefined): string {
  if (!input) return '—';
  const s = input.trim();
  const temTz = /Z$/i.test(s) || /[+-]\d{2}:?\d{2}$/.test(s);
  if (!temTz) {
    // wall-clock literal (data + hora)
    const comHora = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(s);
    if (comHora) {
      const [, y, mo, d, h, mi] = comHora;
      return `${d}/${mo}/${y} às ${h}:${mi}`;
    }
    // só data (evita o shift de meia-noite UTC → dia anterior)
    const soData = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (soData) {
      const [, y, mo, d] = soData;
      return `${d}/${mo}/${y}`;
    }
  }
  // com timezone explícito → instante → relógio de Brasília
  return s.includes('T') ? formatDateTime(s) : formatDate(s);
}

/**
 * Data relativa em pt-BR humanizada — útil pra "última atividade", "criado em", etc.
 *
 * formatRelativeDate(now)                    → "agora há pouco"
 * formatRelativeDate(now - 30s)              → "agora há pouco"
 * formatRelativeDate(now - 5min)             → "há 5 minutos"
 * formatRelativeDate(now - 2h)               → "há 2 horas"
 * formatRelativeDate(now - 1d)               → "ontem"
 * formatRelativeDate(now - 3d)               → "há 3 dias"
 * formatRelativeDate(now - 30d)              → "há 1 mês"
 * formatRelativeDate(now - 400d)             → "há 1 ano"
 *
 * @param nowFn Override pra testes determinísticos
 */
export function formatRelativeDate(
  input: DateInput,
  nowFn: () => Date = () => new Date()
): string {
  const l = ler(input);
  if (!l) return '—';

  const diffMs = nowFn().getTime() - l.d.getTime();
  const future = diffMs < 0;
  const abs = Math.abs(diffMs);

  const SEC = 1000;
  const MIN = 60 * SEC;
  const HOUR = 60 * MIN;
  const DAY = 24 * HOUR;

  // Limiares
  if (abs < 60 * SEC) return future ? 'em instantes' : 'agora há pouco';
  if (abs < HOUR) {
    const mins = Math.round(abs / MIN);
    return future ? `em ${mins} min` : `há ${mins} min`;
  }
  if (abs < DAY) {
    const hrs = Math.round(abs / HOUR);
    return future ? `em ${hrs}h` : `há ${hrs}h`;
  }
  if (abs < 2 * DAY && !future) return 'ontem';
  if (abs < 2 * DAY && future) return 'amanhã';
  if (abs < 30 * DAY) {
    const days = Math.round(abs / DAY);
    return future ? `em ${days} dias` : `há ${days} dias`;
  }
  if (abs < 365 * DAY) {
    const months = Math.round(abs / (30 * DAY));
    if (months === 1) return future ? 'em 1 mês' : 'há 1 mês';
    return future ? `em ${months} meses` : `há ${months} meses`;
  }
  const years = Math.round(abs / (365 * DAY));
  if (years === 1) return future ? 'em 1 ano' : 'há 1 ano';
  return future ? `em ${years} anos` : `há ${years} anos`;
}

// ============================================================
// DURAÇÃO ENTRE DATAS
// ============================================================

/**
 * Calcula dias entre duas datas (ignora horas).
 *
 * daysBetween('2026-05-01', '2026-05-15') → 14
 * daysBetween(today, tomorrow)            → 1
 */
export function daysBetween(start: DateInput, end: DateInput): number | null {
  const a = ler(start);
  const b = ler(end);
  if (!a || !b) return null;
  const ms = b.d.getTime() - a.d.getTime();
  return Math.round(ms / (1000 * 60 * 60 * 24));
}

/**
 * Saudação baseada no horário (Bom dia / Boa tarde / Boa noite).
 *
 * greeting(new Date(2026, 4, 16, 9))  → "Bom dia"
 * greeting(new Date(2026, 4, 16, 15)) → "Boa tarde"
 * greeting(new Date(2026, 4, 16, 22)) → "Boa noite"
 */
export function greeting(now: Date = new Date()): 'Bom dia' | 'Boa tarde' | 'Boa noite' {
  const h = horaEmBrasilia(now).hora;
  if (h < 12) return 'Bom dia';
  if (h < 18) return 'Boa tarde';
  return 'Boa noite';
}

const MESES_POR_EXTENSO = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];

/**
 * `2026-08` → `agosto de 2026`. Para frase de gente, não para eixo de gráfico.
 *
 * ⚠️ Devolve `null` quando não há mês — ❌ nunca "o mês de hoje". Card que não
 * fechou não tem mês, e a tela precisa poder dizer isso em vez de inventar.
 *
 * ⚠️ E ela NÃO usa `Date`: `'2026-08'` interpretado como data vira
 * `2026-08-01T00:00Z`, que em Brasília é **julho**. É a mesma armadilha do
 * "agendar 14:00 saiu 12:00".
 */
export function mesPorExtenso(mes: string | null | undefined): string | null {
  if (!mes) return null;
  const m = /^(\d{4})-(\d{2})$/.exec(mes);
  if (!m) return null;
  const i = Number(m[2]) - 1;
  if (i < 0 || i > 11) return null;
  return `${MESES_POR_EXTENSO[i]} de ${m[1]}`;
}
